import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Collapse,
} from '@mui/material';
import {
    Close as CloseIcon,
    Refresh as RefreshIcon,
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TaxAuthorityService from '../../Services/taxAuthority.service';
import {
    rowAwaitsDocument,
    rowHasDecisionAlreadyOnFile,
} from './problemInvoiceRowState';

const pickAmount = (...candidates) => {
    for (const c of candidates) {
        if (c === null || c === undefined || c === '' || Array.isArray(c) || typeof c === 'object') continue;
        const n = Number(c);
        if (Number.isFinite(n)) return n;
    }
    return undefined;
};

const formatAmount = (v) => {
    if (v === null || v === undefined || v === '' || Array.isArray(v)) return '—';
    if (typeof v === 'object') return '—';
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v);
};

/** Build amounts line with a translated VAT label (not hardcoded English "VAT"). */
const amountSummaryFromPayload = (payload, vatLabel) => {
    if (!payload || typeof payload !== 'object') return null;
    const before = pickAmount(
        payload.amount_before_discount,
        payload.Amount_Before_Discount,
        payload.payment_amount,
        payload.Payment_Amount
    );
    const incl = pickAmount(
        payload.payment_amount_including_vat,
        payload.Payment_Amount_Including_VAT
    );
    const vat = pickAmount(payload.vat_amount, payload.VAT_Amount);
    if (before == null && incl == null && vat == null) return null;
    return `${formatAmount(before)} / ${formatAmount(incl)} (${vatLabel} ${formatAmount(vat)})`;
};

/** Backend may still return "… (VAT 1800)" — replace English VAT with translated label. */
const localizeAmountSummary = (summary, vatLabel) => {
    if (!summary) return null;
    return String(summary).replace(/\(\s*VAT\s+/gi, `(${vatLabel} `);
};

/**
 * 432 = the customer's VAT number on the invoice is wrong. Nothing is held at the Tax Authority and
 * there is no decision to send — the invoice has to be reissued with a correct number — so these
 * rows do not belong in the problem-invoice list.
 */
const CUSTOMER_VAT_NUMBER_ERROR_CODE = 432;

const isCustomerVatNumberError = (row) => {
    const code = row?.errorCode ?? row?.ErrorCode;
    if (code === null || code === undefined || code === '') return false;
    return Number(code) === CUSTOMER_VAT_NUMBER_ERROR_CODE;
};

const getAttemptForDetails = (row) => {
    const history = row.confirmationHistory || row.ConfirmationHistory || [];
    if (!Array.isArray(history) || history.length === 0) return null;
    const firstFailed = history
        .filter((a) => !(a.isSuccess ?? a.IsSuccess))
        .sort(
            (a, b) =>
                new Date(a.creationDate || a.CreationDate || 0) -
                new Date(b.creationDate || b.CreationDate || 0)
        )[0];
    return firstFailed || history[0];
};

const extractInvoiceDetails = (row) => {
    const attempt = getAttemptForDetails(row);
    const requestPayload = attempt?.requestPayload || attempt?.RequestPayload || null;
    const invoicePayload = attempt?.invoicePayload || attempt?.InvoicePayload || null;

    const rawItems = invoicePayload?.Items || invoicePayload?.items;
    const items = Array.isArray(rawItems)
        ? rawItems.filter((it) => it && typeof it === 'object' && !Array.isArray(it))
        : [];

    return {
        requestPayload,
        invoicePayload,
        items,
        clientName:
            invoicePayload?.ClientName ||
            invoicePayload?.clientName ||
            invoicePayload?.Name ||
            invoicePayload?.name ||
            null,
        description: invoicePayload?.Description || invoicePayload?.description || null,
        amountBefore: pickAmount(
            requestPayload?.amount_before_discount,
            requestPayload?.Amount_Before_Discount,
            requestPayload?.payment_amount,
            requestPayload?.Payment_Amount
        ),
        amountInclVat: pickAmount(
            requestPayload?.payment_amount_including_vat,
            requestPayload?.Payment_Amount_Including_VAT
        ),
        vatAmount: pickAmount(requestPayload?.vat_amount, requestPayload?.VAT_Amount),
    };
};

/**
 * Overview modal: all Tax Authority confirmation failures for a case + decision status.
 */
const TaxAuthorityProblemInvoicesModal = ({
    open,
    onClose,
    caseId,
    token,
    onOpenDecision,
    onFinishFromSnapshot,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'he' || i18n.language === 'ar';
    const direction = i18n.dir();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const vatLabel = t('VAT') || 'VAT';

    const load = useCallback(async () => {
        if (!caseId || !token) return;
        setLoading(true);
        setError(null);
        try {
            const res = await TaxAuthorityService.getProblemInvoiceDecisions(caseId, token);
            const fetched = res?.items || res?.Items || [];
            setItems(fetched.filter((row) => !isCustomerVatNumberError(row)));
        } catch (e) {
            console.error(e);
            setError(e?.message || t('Error') || 'Error');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [caseId, token, t]);

    useEffect(() => {
        if (open) {
            load();
            setExpandedId(null);
        }
    }, [open, load]);

    const formatDateTime = (value) => {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return String(value);
        }
    };

    const translateTaxErrorCode = (errorCode) => {
        if (errorCode == null || errorCode === '') return null;
        const key = `TaxInvoiceError${errorCode}`;
        const translated = t(key);
        if (translated && translated !== key) return translated;
        return null;
    };

    const translateStatusKey = (status) => {
        if (!status) return null;
        const map = {
            NeedsDecision: t('NeedsDecision') || 'Needs decision',
            DecisionAccepted: t('DecisionAccepted') || 'Decision accepted',
            DecisionRejected: t('DecisionRejected') || 'Decision rejected',
            ConfirmationFailed: t('ConfirmationFailed') || 'Confirmation failed',
            AwaitingDocument: t('AwaitingDocument') || t('TaxAuthorityDecisionSent') || 'Decision sent — document not issued',
            DecidedElsewhere: t('DecidedElsewhere') || 'Decision already sent at the Tax Authority',
            None: t('NoDecisionRequired') || t('StatusNone') || 'No decision required',
        };
        return map[status] || null;
    };

    const translateMessage = (msg, errorCode) => {
        if (!msg && (errorCode == null || errorCode === '')) return '—';
        const codeTranslated = translateTaxErrorCode(errorCode);
        if (codeTranslated) return codeTranslated;
        if (!msg) return '—';
        const lower = String(msg).toLowerCase().trim();
        if (lower === 'invoice not approved') return t('TaxInvoiceError460') || msg;
        if (lower === 'customer vat number is incorrect') return t('TaxInvoiceError432') || msg;
        if (lower === 'decision accepted') return t('DecisionAccepted') || msg;
        if (lower === 'operation completed successfully') return t('DecisionAccepted') || msg;
        if (lower === 'no matching unapproved invoice found') return t('TaxInvoiceError463') || msg;
        return msg;
    };

    const sourceLabel = (row) => {
        const source = row.source || row.Source || '';
        const docType = row.documentType || row.DocumentType || '';
        const key = (source || docType || '').toString();
        const lower = key.toLowerCase();
        if (
            lower === 'invoicereceipt' ||
            lower === 'receipttax' ||
            lower.includes('receipt')
        ) {
            return t('InvoiceReceipt') || t('TaxInvoiceReceipt') || 'Invoice receipt';
        }
        if (lower === 'creditinvoice' || lower === 'creditnote' || lower.includes('credit')) {
            return t('CreditTaxInvoice') || t('CreditNote') || 'Credit invoice';
        }
        if (lower === 'invoice' || lower === 'taxinvoice') {
            return t('TaxInvoice') || t('Invoice') || 'Invoice';
        }
        return source || docType || t('TaxInvoice') || 'Invoice';
    };

    const formatRowAmounts = (row) => {
        const details = extractInvoiceDetails(row);
        const fromPayload = amountSummaryFromPayload(details.requestPayload, vatLabel);
        if (fromPayload) return fromPayload;
        return (
            localizeAmountSummary(row.amountSummary || row.AmountSummary, vatLabel) || '—'
        );
    };

    const isHeldInvoiceDecisionCode = (errorCode) => {
        const n = Number(errorCode);
        // SHAAM: only 460/461 require Cancel/Continue/Hearing decision (§2.2.2 / §4)
        // 406 Not Acceptable is technical (vat_number permission) — not a decision
        return n === 460 || n === 461;
    };

    const rowRequiresDecision = (row) => {
        const errorCode = row.errorCode ?? row.ErrorCode;
        const requiresRaw = !!(row.requiresDecision ?? row.RequiresDecision);
        if (!requiresRaw) return false;
        // If backend forgot error code but marks NeedsDecision, still trust for legacy pending
        if (errorCode == null || errorCode === '') return true;
        return isHeldInvoiceDecisionCode(errorCode);
    };

    const statusChip = (row) => {
        const errorCode = row.errorCode ?? row.ErrorCode;
        const requires = rowRequiresDecision(row);
        const status = row.status || row.Status || '';
        // 464 / already-on-file must never read as "Decision rejected" — SHAAM only told us a
        // decision exists; it did not say the decision failed.
        if (rowAwaitsDocument(row)) {
            return (
                <Chip
                    size="small"
                    color="info"
                    label={
                        t('AwaitingDocument') ||
                        t('TaxAuthorityDecisionSent') ||
                        'Decision sent — document not issued'
                    }
                />
            );
        }
        if (status === 'DecidedElsewhere') {
            return (
                <Chip
                    size="small"
                    color="default"
                    label={
                        t('DecidedElsewhere') ||
                        t('TaxAuthorityDecisionSent') ||
                        'Decision already sent at the Tax Authority'
                    }
                />
            );
        }
        if (requires || (status === 'NeedsDecision' && isHeldInvoiceDecisionCode(errorCode))) {
            return (
                <Chip
                    size="small"
                    color="warning"
                    label={t('NeedsDecision') || 'Needs decision'}
                />
            );
        }
        if (status === 'DecisionAccepted' || rowHasDecisionAlreadyOnFile(row)) {
            return (
                <Chip
                    size="small"
                    color="success"
                    label={
                        rowHasDecisionAlreadyOnFile(row) && status !== 'DecisionAccepted'
                            ? t('TaxAuthorityDecisionSent') ||
                              t('DecisionAccepted') ||
                              'Decision sent'
                            : t('DecisionAccepted') || 'Decision accepted'
                    }
                />
            );
        }
        if (status === 'DecisionRejected') {
            return (
                <Chip
                    size="small"
                    color="error"
                    label={t('DecisionRejected') || 'Decision rejected'}
                />
            );
        }

        // Non-460/461: show translated TA error code message in Status
        const codeTranslated = translateTaxErrorCode(errorCode);
        if (codeTranslated) {
            return (
                <Tooltip title={codeTranslated}>
                    <Chip
                        size="small"
                        color="default"
                        label={codeTranslated}
                        sx={{
                            maxWidth: 280,
                            height: 'auto',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                display: 'block',
                                py: 0.5,
                            },
                        }}
                    />
                </Tooltip>
            );
        }

        const fallbackStatus =
            translateStatusKey(status) ||
            (errorCode != null && errorCode !== ''
                ? `${t('Error') || 'Error'} ${errorCode}`
                : status || '—');
        return (
            <Chip
                size="small"
                color="default"
                label={fallbackStatus}
                sx={{
                    maxWidth: 280,
                    height: 'auto',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        display: 'block',
                        py: 0.5,
                    },
                }}
            />
        );
    };

    const toggleExpand = (invId) => {
        setExpandedId((prev) => (prev === invId ? null : invId));
    };

    const renderExpandedDetails = (row) => {
        const details = extractInvoiceDetails(row);
        const { items: lineItems, clientName, description, amountBefore, amountInclVat, vatAmount } =
            details;

        return (
            <Box sx={{ py: 1.5, px: 1, direction }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {t('InvoiceDetails') || t('OriginalLockedInvoiceDetails') || 'Invoice details'}
                </Typography>

                {(clientName || description || amountBefore != null || amountInclVat != null) && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 0.5,
                            mb: lineItems.length ? 1.5 : 0,
                        }}
                    >
                        {clientName && (
                            <Typography variant="body2">
                                {t('ClientName') || t('Customer') || 'Client'}:{' '}
                                <strong>{clientName}</strong>
                            </Typography>
                        )}
                        {description && (
                            <Typography variant="body2">
                                {t('Description') || 'Description'}:{' '}
                                <strong>{description}</strong>
                            </Typography>
                        )}
                        <Typography variant="body2">
                            {t('AmountBeforeDiscount') || 'Amount before discount'}:{' '}
                            <strong>{formatAmount(amountBefore)}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {t('PaymentIncludingVAT') || 'Payment incl. VAT'}:{' '}
                            <strong>{formatAmount(amountInclVat)}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {t('VATAmount') || vatLabel}:{' '}
                            <strong>{formatAmount(vatAmount)}</strong>
                        </Typography>
                    </Box>
                )}

                {lineItems.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('ItemName') || t('Name') || 'Name'}</TableCell>
                                    <TableCell>{t('Makat') || t('SKU') || 'SKU'}</TableCell>
                                    <TableCell align="right">
                                        {t('Quantity') || 'Qty'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t('Price') || 'Price'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t('Total') || 'Total'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lineItems.map((it, idx) => {
                                    const qty = Number(it.Quantity ?? it.quantity ?? 1) || 1;
                                    const price = Number(it.Price ?? it.price ?? 0) || 0;
                                    const name =
                                        it.Name || it.name || it.Makat || it.makat || `#${idx + 1}`;
                                    const makat = it.Makat || it.makat || '—';
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{name}</TableCell>
                                            <TableCell>{makat}</TableCell>
                                            <TableCell align="right">{qty}</TableCell>
                                            <TableCell align="right">
                                                {formatAmount(price)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatAmount(price * qty)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {t('NoInvoiceItems') ||
                            t('NoItems') ||
                            'No line items available for this invoice attempt.'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
                dir={direction}
            >
                <Typography variant="h6" fontWeight={700} sx={{ direction }}>
                    {t('TaxAuthorityProblemInvoices') || 'Tax Authority — problem invoices'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={t('Refresh') || 'Refresh'}>
                        <IconButton onClick={load} disabled={loading} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ direction }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : items.length === 0 ? (
                    <Typography color="text.secondary">
                        {t('NoProblemInvoices') ||
                            'No rejected confirmation attempts found for this case.'}
                    </Typography>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell>{t('InvoiceReference') || 'Reference'}</TableCell>
                                    <TableCell>{t('DocumentType') || t('Source') || 'Source'}</TableCell>
                                    <TableCell>{t('Status') || 'Status'}</TableCell>
                                    <TableCell>{t('ErrorCode') || 'Error'}</TableCell>
                                    <TableCell>{t('Message') || 'Message'}</TableCell>
                                    <TableCell>{t('Amounts') || 'Amounts'}</TableCell>
                                    <TableCell>
                                        {t('FailedAttempts') || 'Failed attempts'}
                                    </TableCell>
                                    <TableCell>
                                        {t('LatestDecision') || 'Latest decision'}
                                    </TableCell>
                                    <TableCell>{t('Date') || 'Date'}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((row) => {
                                    const invId = row.invoiceId || row.InvoiceId;
                                    const docType =
                                        row.documentType || row.DocumentType || 'TaxInvoice';
                                    const rowKey = `${invId}|${docType}`;
                                    const requires = rowRequiresDecision(row);
                                    // The decision is already on file at the Tax Authority; what is
                                    // missing is the document itself, so the row offers to finish
                                    // it rather than to send another decision.
                                    const awaitsDocument = rowAwaitsDocument(row);
                                    // Never treat 464 as "rejected" — we only know a decision was
                                    // sent, whether or not the document was issued afterwards.
                                    const decisionOnFile = rowHasDecisionAlreadyOnFile(row);
                                    const action =
                                        row.acceptedDecisionAction ||
                                        row.AcceptedDecisionAction ||
                                        row.latestDecisionAction ||
                                        row.LatestDecisionAction ||
                                        '';
                                    const decOk = decisionOnFile
                                        ? true
                                        : row.latestDecisionSuccess ?? row.LatestDecisionSuccess;
                                    const isExpanded = expandedId === rowKey;
                                    return (
                                        <React.Fragment key={rowKey}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '& > *': {
                                                        borderBottom: isExpanded
                                                            ? 'unset'
                                                            : undefined,
                                                    },
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => toggleExpand(rowKey)}
                                            >
                                                <TableCell sx={{ width: 40, p: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={
                                                            isExpanded ? 'collapse' : 'expand'
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpand(rowKey);
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLessIcon fontSize="small" />
                                                        ) : (
                                                            <ExpandMoreIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>{invId}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={sourceLabel(row)}
                                                    />
                                                </TableCell>
                                                <TableCell>{statusChip(row)}</TableCell>
                                                <TableCell>
                                                    {row.errorCode ?? row.ErrorCode ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {translateMessage(
                                                        row.errorMessage || row.ErrorMessage,
                                                        row.errorCode ?? row.ErrorCode
                                                    )}
                                                </TableCell>
                                                <TableCell>{formatRowAmounts(row)}</TableCell>
                                                <TableCell>
                                                    {row.failedAttempts ??
                                                        row.FailedAttempts ??
                                                        0}
                                                </TableCell>
                                                <TableCell>
                                                    {action ? (
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {t(`DecisionAction_${action}`) ||
                                                                    action}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color={
                                                                    decOk
                                                                        ? 'success.main'
                                                                        : 'error.main'
                                                                }
                                                            >
                                                                {decOk
                                                                    ? decisionOnFile
                                                                        ? t('TaxAuthorityDecisionSent') ||
                                                                          t('ApprovedDecision') ||
                                                                          'Decision sent'
                                                                        : t('DecisionAccepted') ||
                                                                          'Accepted'
                                                                    : t('DecisionRejected') ||
                                                                      'Rejected'}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {t('NoDecisionsSentYet') ||
                                                                'No decisions sent yet'}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateTime(
                                                        row.lastAttemptAt ||
                                                            row.LastAttemptAt ||
                                                            row.firstFailedAt ||
                                                            row.FirstFailedAt
                                                    )}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    {(requires || awaitsDocument) &&
                                                        (onOpenDecision || onFinishFromSnapshot) && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color={awaitsDocument ? 'primary' : 'warning'}
                                                            onClick={() => {
                                                                if (awaitsDocument && onFinishFromSnapshot) {
                                                                    const acceptedAction =
                                                                        row.acceptedDecisionAction ||
                                                                        row.AcceptedDecisionAction;
                                                                    const flag =
                                                                        TaxAuthorityService.mapOptionToDecisionFlag(
                                                                            acceptedAction
                                                                        );
                                                                    if (flag !== null) {
                                                                        onFinishFromSnapshot(
                                                                            row,
                                                                            flag
                                                                        );
                                                                        return;
                                                                    }
                                                                }
                                                                if (onOpenDecision) onOpenDecision(row);
                                                            }}
                                                        >
                                                            {awaitsDocument
                                                                ? t('FinishPendingInvoice') ||
                                                                  'Issue document'
                                                                : t('ResolveDecision') || 'Resolve'}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={11}
                                                    sx={{
                                                        py: 0,
                                                        backgroundColor: 'action.hover',
                                                    }}
                                                >
                                                    <Collapse
                                                        in={isExpanded}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        {renderExpandedDetails(row)}
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ direction, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <Button onClick={onClose} variant="outlined">
                    {t('Close') || t('Cancel') || 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaxAuthorityProblemInvoicesModal;
